import React, { useState } from 'react';
import {
  Dialog, DialogContent, Box, Typography, IconButton, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, Divider, Chip, Paper,
  useTheme, useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Groups as GroupsIcon,
  MeetingRoom as RoomIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as AttendanceIcon,
  Assessment as ReportsIcon,
  MenuBook as ManualIcon,
  ArrowForwardIos as ArrowIcon,
  Lightbulb as TipIcon,
  Info as InfoIcon,
  Star as StarIcon,
  Construction as ComingSoonIcon,
  LocalShipping as ParcIcon,
  Inventory as StockIcon,
  Factory as MakerIcon,
  CalendarMonth as PlanningIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Drafts as EmailTemplateIcon,
  Tune as ControlsIcon,
  LocalOffer as DiscountIcon,
  PersonAdd as PreRegIcon,
  Quiz as QuizIcon,
  MarkEmailRead as EmailLogIcon,
  EventAvailable as RoomRentalIcon,
  SupportAgent as CrmIcon,
  Assignment as ProjectIcon,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { useApplication, ApplicationName } from '../contexts/ApplicationContext';
import { CRM_SECTIONS, CRM_CONTENT } from '../../CRM/config/crmUserManual';
import { PROJECT_SECTIONS, PROJECT_CONTENT } from '../../ProjectManagement/config/projectUserManual';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface ManualSection {
  id: string;
  icon: React.ReactNode;
  color: string;
}

export interface SectionContent {
  title: string;
  description: string;
  features: string[];
  tips: string[];
  steps?: string[];
}

export type LangKey = 'fr' | 'en' | 'ar';
export type LangMap = Record<string, SectionContent>;
export type AppManualContent = Record<LangKey, LangMap>;

// ─────────────────────────────────────────────────────────────────────────────
// Academy — Section definitions
// ─────────────────────────────────────────────────────────────────────────────

const ACADEMY_SECTIONS: ManualSection[] = [
  { id: 'dashboard',         icon: <DashboardIcon />,     color: '#4f46e5' },
  { id: 'students',          icon: <PeopleIcon />,         color: '#0284c7' },
  { id: 'pre-registrations', icon: <PreRegIcon />,         color: '#0891b2' },
  { id: 'teachers',          icon: <SchoolIcon />,         color: '#7c3aed' },
  { id: 'groups',            icon: <GroupsIcon />,         color: '#d97706' },
  { id: 'rooms',             icon: <RoomIcon />,           color: '#be185d' },
  { id: 'room-rentals',      icon: <RoomRentalIcon />,     color: '#9d174d' },
  { id: 'scheduler',         icon: <ScheduleIcon />,       color: '#0f766e' },
  { id: 'attendance',        icon: <AttendanceIcon />,     color: '#dc2626' },
  { id: 'financial',         icon: <MoneyIcon />,          color: '#059669' },
  { id: 'discounts',         icon: <DiscountIcon />,       color: '#b45309' },
  { id: 'quizzes',           icon: <QuizIcon />,           color: '#7c3aed' },
  { id: 'reports',           icon: <ReportsIcon />,        color: '#6d28d9' },
  { id: 'email-templates',   icon: <EmailTemplateIcon />,  color: '#1d4ed8' },
  { id: 'email-log',         icon: <EmailLogIcon />,       color: '#475569' },
  { id: 'planning-controls', icon: <ControlsIcon />,       color: '#0f172a' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Academy — Multilingual content
// ─────────────────────────────────────────────────────────────────────────────

const ACADEMY_CONTENT: AppManualContent = {
  fr: {
    dashboard: {
      title: 'Tableau de Bord',
      description: "Le tableau de bord est votre centre de contrôle principal. Il affiche en temps réel les indicateurs clés de performance (KPI) de votre académie et vous permet d'agir rapidement sur les priorités du jour.",
      features: [
        'KPI en direct : étudiants actifs, enseignants, salles, revenus mensuels',
        'Widget dynamique de dette (vert / orange / rouge selon le seuil)',
        "Planning du jour avec les séances triées par heure de début",
        "Alertes de présence faible (< 75%) sur les séances de la veille",
        'Recherche globale d\'étudiants depuis la barre de recherche',
        'Rafraîchissement automatique toutes les 5 minutes',
      ],
      tips: [
        'Cliquez sur un KPI pour naviguer directement vers la section correspondante.',
        'Le widget rouge de dette signale plus de 5 000 DZD impayés — agissez sans délai.',
        'Utilisez le bouton "Rafraîchir" pour forcer une mise à jour immédiate des données.',
      ],
      steps: [
        'Consultez les 4 KPI du haut pour un aperçu rapide.',
        'Vérifiez le widget de dette : vert = sain, orange = attention, rouge = critique.',
        'Parcourez le planning du jour et prenez les présences manquantes.',
        'Traitez les alertes de faible présence en bas à droite.',
      ],
    },
    students: {
      title: 'Étudiants',
      description: "Gérez l'ensemble de vos étudiants inscrits : création, modification, suivi financier et historique de présence. Chaque fiche étudiant est reliée aux groupes, paiements et séances.",
      features: [
        'Liste paginée et triable de tous les étudiants',
        'Fiche individuelle : informations personnelles, groupe(s) assigné(s)',
        'Suivi des paiements par étudiant (montant dû vs. payé)',
        'Historique de présence par étudiant',
        'Recherche rapide par nom, prénom ou numéro de téléphone',
        'Ajout et modification via formulaire intégré',
      ],
      tips: [
        "Utilisez la recherche globale (barre du tableau de bord) pour trouver un étudiant depuis n'importe quelle page.",
        'Un étudiant peut être inscrit dans plusieurs groupes simultanément.',
        'Le champ "Statut" permet de filtrer facilement les actifs des inactifs.',
      ],
    },
    teachers: {
      title: 'Enseignants',
      description: "Centralisez la gestion de vos enseignants : profils, disponibilités et affectation aux groupes. Chaque enseignant est lié aux groupes qu'il supervise.",
      features: [
        'Liste complète des enseignants avec statut actif / inactif',
        'Fiche enseignant : coordonnées, spécialité, groupes assignés',
        'Filtre par statut et spécialité',
        "Lien direct vers le planning d'un enseignant",
      ],
      tips: [
        "Désactivez un enseignant plutôt que de le supprimer pour conserver l'historique.",
        "Un enseignant inactif n'apparaît plus dans les listes de sélection du planificateur.",
      ],
    },
    groups: {
      title: 'Groupes',
      description: "Organisez vos étudiants en groupes de cours. Chaque groupe est défini par une matière, un niveau, un enseignant et une liste d'étudiants inscrits.",
      features: [
        'Création et modification de groupes',
        "Assignation d'un enseignant responsable par groupe",
        'Liste des étudiants membres du groupe',
        'Tarif mensuel par groupe (utilisé pour la facturation automatique)',
        'Indicateur du nombre de séances planifiées',
      ],
      tips: [
        'Définissez un tarif mensuel sur chaque groupe pour que le module financier calcule automatiquement les dettes.',
        'Renommez vos groupes de façon explicite (ex. : "Maths - Niveau 3ème") pour faciliter la lecture du planning.',
      ],
    },
    rooms: {
      title: 'Salles',
      description: "Gérez les salles de cours disponibles dans votre académie. Le système vérifie les conflits de réservation lors de la création de séances.",
      features: [
        'Liste des salles avec numéro, étage et capacité',
        'Statut : Actif / Inactif',
        'Vérification automatique des conflits lors du planning',
        "Indicateur d'utilisation (nombre de séances planifiées)",
      ],
      tips: [
        'Marquez une salle comme "Inactive" lors de travaux ou indisponibilité pour éviter les réservations.',
        "La capacité de la salle est indicative et n'empêche pas la sur-réservation — à vous de gérer.",
      ],
    },
    scheduler: {
      title: 'Planificateur',
      description: "Planifiez et gérez toutes les séances de cours. Le planificateur détecte les conflits de salle et d'enseignant en temps réel.",
      features: [
        'Vue calendrier : semaine ou jour',
        "Création de séances avec groupe, salle, enseignant, horaire",
        'Détection automatique des conflits (salle ou enseignant déjà occupé)',
        '"Présences prises" marqué par séance',
        'Filtres par groupe, salle ou enseignant',
      ],
      tips: [
        "Une séance ne peut pas être créée si la salle ou l'enseignant est déjà occupé sur ce créneau.",
        'Les séances du jour apparaissent automatiquement sur le tableau de bord.',
        'Cochez "Présences prises" directement depuis le planificateur après chaque séance.',
      ],
      steps: [
        'Cliquez sur un créneau libre dans le calendrier.',
        "Sélectionnez le groupe, la salle et l'enseignant.",
        'Définissez les heures de début et de fin.',
        "Enregistrez — le système valide l'absence de conflit.",
      ],
    },
    financial: {
      title: 'Finance',
      description: "Suivez les paiements, les dettes et les revenus de l'académie. Le module financier calcule automatiquement ce que chaque étudiant doit en fonction de son groupe et du tarif mensuel.",
      features: [
        'Vue d\'ensemble des revenus du mois en cours',
        'Liste des étudiants débiteurs avec montant dû',
        'Enregistrement de paiements (partiels ou complets)',
        'Historique complet des transactions par étudiant',
        'Indicateur de dette totale (repris sur le tableau de bord)',
      ],
      tips: [
        'Le tableau de bord affiche la dette totale en rouge si elle dépasse 5 000 DZD — traitement prioritaire conseillé.',
        'Enregistrez les paiements partiels pour maintenir un suivi précis.',
        'Utilisez les filtres pour isoler les étudiants avec dette > 0.',
      ],
    },
    attendance: {
      title: 'Présences',
      description: "Enregistrez et consultez les présences pour chaque séance de cours. Chaque étudiant d'un groupe peut être marqué Présent, Absent ou en Retard.",
      features: [
        'Sélection de la séance du jour à pointer',
        'Liste automatique des étudiants du groupe',
        'Statuts : Présent / Absent / Retard',
        'Marquage rapide "Tous présents"',
        'Consultation de l\'historique des présences par étudiant',
        'Taux de présence calculé automatiquement par séance',
      ],
      tips: [
        'Accédez aux présences directement depuis le tableau de bord en cliquant sur "Prendre les présences".',
        'Les séances avec moins de 75% de présence déclenchent une alerte sur le tableau de bord le lendemain.',
        'Une séance doit être créée dans le planificateur avant de pouvoir y saisir des présences.',
      ],
      steps: [
        'Allez dans le module Présences.',
        'Sélectionnez la date et la séance souhaitée.',
        'Cochez le statut de chaque étudiant.',
        'Enregistrez — la séance est automatiquement marquée "Présences prises".',
      ],
    },
    reports: {
      title: 'Rapports',
      description: "Générez des rapports et des statistiques sur l'activité de l'académie : présences, paiements, groupes et enseignants.",
      features: [
        'Rapport de présence global ou par groupe / étudiant',
        'Rapport financier : revenus, dettes, paiements par période',
        "Rapport d'activité des enseignants",
        'Export possible des données',
      ],
      tips: [
        'Filtrez par période (mois, trimestre) pour des analyses précises.',
        "Partagez les rapports d'assiduité avec les parents pour un suivi transparent.",
      ],
    },
    'pre-registrations': {
      title: 'Pré-inscriptions',
      description: "Gérez les demandes de candidature avant admission définitive. Chaque demande suit un flux : soumise → acceptée → convertie en étudiant (ou refusée).",
      features: [
        'Formulaire complet de pré-inscription (coordonnées, cours souhaité)',
        'Statuts : En attente / Accepté / Refusé',
        'Conversion en fiche étudiant en un clic (si accepté)',
        'Email automatique au candidat selon la décision prise',
      ],
      tips: [
        'Acceptez une pré-inscription puis cliquez "Convertir" pour créer la fiche étudiant complète automatiquement.',
        'Un email de notification est envoyé si un modèle d\'email correspondant est actif.',
      ],
      steps: [
        'Ouvrez la pré-inscription reçue.',
        'Vérifiez les informations et ajoutez des notes internes si besoin.',
        'Cliquez "Accepter" ou "Refuser".',
        'Si accepté, cliquez "Convertir en étudiant" pour finaliser l\'inscription.',
      ],
    },
    'room-rentals': {
      title: 'Location de Salles',
      description: "Gérez la location de salles à des clients externes, en dehors des cours académiques. Le système vérifie les conflits avec le planning existant.",
      features: [
        'Réservation de salle pour client externe avec plage horaire',
        'Vérification automatique des conflits avec le planning académique',
        'Fiche client locataire (nom, contact, tarif)',
        'Visualisation des disponibilités en temps réel',
      ],
      tips: [
        'La salle apparaît comme occupée dans le planning académique pendant la location.',
        'Définissez un tarif de location pour un suivi financier précis.',
      ],
    },
    discounts: {
      title: 'Remises & Réductions',
      description: "Créez et gérez les types de remises applicables aux tarifs des étudiants. Chaque remise est définie par un type (pourcentage ou montant fixe) et une période de validité.",
      features: [
        'Création de remises en pourcentage ou montant fixe',
        'Dates de validité (début / fin)',
        'Application par étudiant depuis la fiche tarification',
        'Flux de validation : soumise → approuvée → appliquée à la facture',
        'Historique des remises accordées par étudiant',
      ],
      tips: [
        'Les remises doivent être approuvées par un responsable avant d\'être effectives.',
        'Consultez la page "Validation des Remises" pour traiter les demandes en attente.',
        'Une remise expirée n\'est plus applicable, même si elle est encore active.',
      ],
    },
    quizzes: {
      title: 'Quiz & Évaluations',
      description: "Créez des évaluations interactives pour vos groupes. Les étudiants répondent aux questions et leurs scores sont enregistrés automatiquement.",
      features: [
        'Création de quiz avec titre, niveau, durée limite et score de passage',
        'Questions : QCM, Vrai/Faux, réponse libre',
        'Activation / désactivation du quiz pour les étudiants',
        'Consultation des tentatives et scores par étudiant',
        'Statistiques de réussite par quiz',
      ],
      tips: [
        'Définissez un score de passage pour distinguer automatiquement les étudiants réussis.',
        'Désactivez un quiz pour l\'éditer sans affecter les étudiants en cours.',
        'Consultez les tentatives pour identifier les questions avec le plus d\'erreurs.',
      ],
      steps: [
        'Créez le quiz (titre, niveau, durée).',
        'Ajoutez les questions une à une depuis l\'onglet Questions.',
        'Activez le quiz — il devient visible pour les étudiants assignés.',
        'Consultez les résultats dans "Tentatives".',
      ],
    },
    'email-templates': {
      title: 'Modèles d\'Emails',
      description: "Personnalisez les emails automatiques envoyés par l'académie (OTP, rappels de dettes, notifications). Les modèles sont des entités système : modifiables et activables, mais non supprimables.",
      features: [
        'Modèles prédéfinis : OTP, rappel de dette adultes, rappel de dette enfants',
        'Éditeur HTML enrichi avec mise en forme (gras, listes, tableaux)',
        'Variables dynamiques cliquables : {student.fullName}, {agencyName}, etc.',
        'Aperçu en direct du rendu email avec données de démonstration',
        'Support multilingue : un modèle par type et par langue (FR, EN, AR)',
        'Activation / désactivation par modèle',
      ],
      tips: [
        'Cliquez sur une variable dans le panneau pour l\'insérer directement dans le corps HTML.',
        'Il ne peut exister qu\'un seul modèle par combinaison (Type + Langue) — modifiez l\'existant.',
        'Un modèle inactif est ignoré par le système d\'envoi automatique.',
        'L\'aperçu en direct utilise des données fictives — vérifiez que les variables s\'affichent correctement.',
      ],
      steps: [
        'Sélectionnez le modèle à modifier depuis la liste.',
        'Modifiez le sujet et le corps HTML.',
        'Utilisez les variables du panneau latéral pour personnaliser le contenu.',
        'Activez le modèle et enregistrez.',
      ],
    },
    'email-log': {
      title: 'Journal des Emails',
      description: "Consultez l'historique complet de tous les emails envoyés par le système. Identifiez les échecs et relancez les envois si nécessaire.",
      features: [
        'Historique horodaté de chaque email envoyé',
        'Statuts : Envoyé ✅ / Échec ❌ / En attente 🕐',
        'Détail de l\'erreur en cas d\'échec (code SMTP, message)',
        'Filtres par statut, type de modèle et période',
        'Bouton "Renvoyer" pour les emails en échec',
      ],
      tips: [
        'Vérifiez régulièrement le journal pour détecter des problèmes d\'envoi SMTP.',
        'Un email en échec peut être renvoyé manuellement depuis cette page.',
      ],
    },
    'planning-controls': {
      title: 'Contrôles Planning',
      description: "Configurez les règles de validation automatiques du planning. Chaque règle peut être bloquante (refuse la séance) ou non-bloquante (simple avertissement). Ces entités sont système : modifiables mais non supprimables.",
      features: [
        'Liste des règles de validation du planning',
        'Activation / désactivation de chaque règle',
        'Comportement : Bloquant (refus) ou Avertissement (conseil)',
        'Règles prédéfinies : conflit de salle, conflit d\'enseignant, capacité dépassée',
        'Possibilité d\'ajouter des règles personnalisées',
      ],
      tips: [
        'Désactivez une règle temporairement pour autoriser une exception sans la supprimer.',
        'Passez une règle en "non-bloquante" si vous préférez un avertissement plutôt qu\'un refus.',
        'Les règles système (intégrées) ne peuvent pas être supprimées — uniquement modifiées.',
      ],
    },
  },

  en: {
    dashboard: {
      title: 'Dashboard',
      description: "The dashboard is your main control center. It displays real-time key performance indicators (KPIs) for your academy and lets you act quickly on the day's priorities.",
      features: [
        'Live KPIs: active students, teachers, rooms, monthly revenue',
        'Dynamic debt widget (green / orange / red based on threshold)',
        "Today's schedule with sessions sorted by start time",
        "Low attendance alerts (< 75%) for yesterday's sessions",
        'Global student search from the search bar',
        'Auto-refresh every 5 minutes',
      ],
      tips: [
        'Click any KPI card to navigate directly to the corresponding section.',
        'A red debt widget means more than 5,000 DZD unpaid — act promptly.',
        'Use the "Refresh" button to force an immediate data update.',
      ],
      steps: [
        'Check the 4 top KPI cards for a quick overview.',
        'Review the debt widget: green = healthy, orange = warning, red = critical.',
        "Browse today's schedule and take any missing attendance.",
        'Handle low attendance alerts in the bottom-right panel.',
      ],
    },
    students: {
      title: 'Students',
      description: 'Manage all your enrolled students: creation, editing, financial tracking, and attendance history. Each student record is linked to groups, payments, and sessions.',
      features: [
        'Paginated, sortable list of all students',
        'Individual profile: personal info, assigned group(s)',
        'Payment tracking per student (amount owed vs. paid)',
        'Attendance history per student',
        'Quick search by name, first name, or phone number',
        'Add and edit via integrated form',
      ],
      tips: [
        'Use the global search (dashboard search bar) to find a student from any page.',
        'A student can be enrolled in multiple groups simultaneously.',
        'The "Status" field makes it easy to filter active from inactive students.',
      ],
    },
    teachers: {
      title: 'Teachers',
      description: 'Centralize teacher management: profiles, availability, and group assignments. Each teacher is linked to the groups they supervise.',
      features: [
        'Full list of teachers with active / inactive status',
        'Teacher profile: contact details, specialty, assigned groups',
        'Filter by status and specialty',
        "Direct link to a teacher's schedule",
      ],
      tips: [
        'Deactivate a teacher instead of deleting to preserve history.',
        'An inactive teacher will not appear in scheduler selection lists.',
      ],
    },
    groups: {
      title: 'Groups',
      description: 'Organize your students into course groups. Each group is defined by a subject, level, teacher, and a list of enrolled students.',
      features: [
        'Create and edit groups',
        'Assign a responsible teacher per group',
        'List of member students per group',
        'Monthly fee per group (used for automatic billing)',
        'Indicator of planned sessions count',
      ],
      tips: [
        'Set a monthly fee on each group so the financial module automatically calculates debts.',
        'Name groups explicitly (e.g. "Math - Grade 9") for easier schedule reading.',
      ],
    },
    rooms: {
      title: 'Rooms',
      description: 'Manage the classrooms available in your academy. The system checks for booking conflicts when creating sessions.',
      features: [
        'List of rooms with number, floor, and capacity',
        'Status: Active / Inactive',
        'Automatic conflict detection during scheduling',
        'Usage indicator (number of planned sessions)',
      ],
      tips: [
        'Mark a room as "Inactive" during maintenance to prevent bookings.',
        'Room capacity is indicative and does not block over-booking — manage this manually.',
      ],
    },
    scheduler: {
      title: 'Scheduler',
      description: 'Plan and manage all course sessions. The scheduler detects room and teacher conflicts in real time.',
      features: [
        'Calendar view: week or day',
        'Create sessions with group, room, teacher, time slot',
        'Automatic conflict detection (room or teacher already occupied)',
        '"Attendance taken" marking per session',
        'Filters by group, room, or teacher',
      ],
      tips: [
        'A session cannot be created if the room or teacher is already occupied in that time slot.',
        "Today's sessions automatically appear on the dashboard.",
        'Mark "Attendance taken" directly from the scheduler after each session.',
      ],
      steps: [
        'Click a free time slot in the calendar.',
        'Select the group, room, and teacher.',
        'Set start and end times.',
        'Save — the system validates that there are no conflicts.',
      ],
    },
    financial: {
      title: 'Financial',
      description: "Track payments, debts, and academy revenue. The financial module automatically calculates what each student owes based on their group's monthly fee.",
      features: [
        'Overview of current month revenue',
        'List of debtor students with amount owed',
        'Record payments (partial or full)',
        'Full transaction history per student',
        'Total debt indicator (shown on the dashboard)',
      ],
      tips: [
        'The dashboard shows total debt in red when it exceeds 5,000 DZD — prioritize collection.',
        'Record partial payments to maintain accurate tracking.',
        'Use filters to isolate students with debt > 0.',
      ],
    },
    attendance: {
      title: 'Attendance',
      description: 'Record and review attendance for each course session. Each student in a group can be marked Present, Absent, or Late.',
      features: [
        "Select today's session to mark attendance",
        'Automatic list of group students',
        'Statuses: Present / Absent / Late',
        '"Mark all present" quick action',
        'Attendance history per student',
        'Attendance rate calculated automatically per session',
      ],
      tips: [
        'Access attendance directly from the dashboard by clicking "Take Attendance".',
        'Sessions with less than 75% attendance trigger an alert on the dashboard the next day.',
        'A session must be created in the scheduler before attendance can be recorded.',
      ],
      steps: [
        'Go to the Attendance module.',
        'Select the date and desired session.',
        'Check the status for each student.',
        'Save — the session is automatically marked as "Attendance taken".',
      ],
    },
    reports: {
      title: 'Reports',
      description: 'Generate reports and statistics on academy activity: attendance, payments, groups, and teachers.',
      features: [
        'Attendance report — global or by group / student',
        'Financial report: revenue, debts, payments by period',
        'Teacher activity report',
        'Data export available',
      ],
      tips: [
        'Filter by period (month, quarter) for precise analysis.',
        'Share attendance reports with parents for transparent follow-up.',
      ],
    },
    'pre-registrations': {
      title: 'Pre-registrations',
      description: 'Manage candidate applications before final enrollment. Each request follows a workflow: submitted → accepted → converted to student (or rejected).',
      features: [
        'Full pre-registration form (contact details, desired course)',
        'Statuses: Pending / Accepted / Rejected',
        'One-click conversion to a full student record (if accepted)',
        'Automatic email to the candidate based on the decision',
      ],
      tips: [
        'Accept a pre-registration then click "Convert" to automatically create the full student record.',
        'A notification email is sent if a matching email template is active.',
      ],
      steps: [
        'Open the received pre-registration.',
        'Review the information and add internal notes if needed.',
        'Click "Accept" or "Reject".',
        'If accepted, click "Convert to Student" to finalize enrollment.',
      ],
    },
    'room-rentals': {
      title: 'Room Rentals',
      description: 'Manage room rentals to external clients, outside academic sessions. The system checks for conflicts with the existing schedule.',
      features: [
        'Room booking for external client with time slot',
        'Automatic conflict check with the academic schedule',
        'Client record (name, contact, rate)',
        'Real-time availability view',
      ],
      tips: [
        'The room appears as occupied in the academic schedule during the rental.',
        'Define a rental rate for precise financial tracking.',
      ],
    },
    discounts: {
      title: 'Discounts',
      description: 'Create and manage discount types applicable to student fees. Each discount is defined by a type (percentage or fixed amount) and a validity period.',
      features: [
        'Percentage or fixed-amount discounts',
        'Validity dates (start / end)',
        'Application per student from the pricing tab',
        'Approval workflow: submitted → approved → applied to invoice',
        'History of discounts granted per student',
      ],
      tips: [
        'Discounts must be approved by a manager before taking effect.',
        'Check the "Discount Validation" page to process pending requests.',
        'An expired discount cannot be applied, even if still active.',
      ],
    },
    quizzes: {
      title: 'Quizzes & Assessments',
      description: 'Create interactive assessments for your groups. Students answer questions and their scores are automatically recorded.',
      features: [
        'Create quizzes with title, level, time limit, and passing score',
        'Question types: MCQ, True/False, open answer',
        'Activate / deactivate quiz visibility for students',
        'View attempts and scores per student',
        'Success statistics per quiz',
      ],
      tips: [
        'Set a passing score to automatically distinguish students who passed.',
        'Deactivate a quiz to edit it without affecting students in progress.',
        'Review attempts to identify questions with the most errors.',
      ],
      steps: [
        'Create the quiz (title, level, duration).',
        'Add questions one by one from the Questions tab.',
        'Activate the quiz — it becomes visible to assigned students.',
        'Review results in "Attempts".',
      ],
    },
    'email-templates': {
      title: 'Email Templates',
      description: 'Customize the automatic emails sent by the academy (OTP, debt reminders, notifications). Templates are system entities: editable and activatable, but not deletable.',
      features: [
        'Predefined templates: OTP, adult debt reminder, children debt reminder',
        'Rich HTML editor with formatting (bold, lists, tables)',
        'Clickable dynamic variables: {student.fullName}, {agencyName}, etc.',
        'Live preview with demonstration data',
        'Multilingual support: one template per type and language (FR, EN, AR)',
        'Enable / disable per template',
      ],
      tips: [
        'Click a variable in the panel to insert it directly into the HTML body.',
        'Only one template per (Type + Language) combination is allowed — edit the existing one.',
        'An inactive template is ignored by the automatic sending system.',
        'The live preview uses dummy data — verify that variables render correctly.',
      ],
      steps: [
        'Select the template to edit from the list.',
        'Edit the subject and HTML body.',
        'Use variables from the side panel to personalize the content.',
        'Activate the template and save.',
      ],
    },
    'email-log': {
      title: 'Email Journal',
      description: 'View the complete history of all emails sent by the system. Identify failures and re-send if necessary.',
      features: [
        'Timestamped history of every sent email',
        'Statuses: Sent ✅ / Failed ❌ / Pending 🕐',
        'Error details on failure (SMTP code, message)',
        'Filters by status, template type, and period',
        '"Resend" button for failed emails',
      ],
      tips: [
        'Check the journal regularly to detect SMTP sending issues.',
        'A failed email can be manually resent from this page.',
      ],
    },
    'planning-controls': {
      title: 'Planning Controls',
      description: 'Configure automatic schedule validation rules. Each rule can be blocking (refuses the session) or non-blocking (warning only). These are system entities: editable but not deletable.',
      features: [
        'List of schedule validation rules',
        'Enable / disable each rule',
        'Behavior: Blocking (refusal) or Warning (advisory)',
        'Predefined rules: room conflict, teacher conflict, capacity exceeded',
        'Custom rule creation',
      ],
      tips: [
        'Temporarily disable a rule to allow an exception without deleting it.',
        'Switch a rule to "non-blocking" if you prefer a warning over a refusal.',
        'Built-in system rules cannot be deleted — only modified.',
      ],
    },
  },

  ar: {
    dashboard: {
      title: 'لوحة التحكم',
      description: 'لوحة التحكم هي مركز القيادة الرئيسي. تعرض مؤشرات الأداء الرئيسية للأكاديمية في الوقت الفعلي وتتيح لك التصرف السريع على أولويات اليوم.',
      features: [
        'مؤشرات الأداء: الطلاب النشطون، المعلمون، القاعات، الإيرادات الشهرية',
        'أداة الديون الديناميكية (أخضر / برتقالي / أحمر حسب العتبة)',
        'جدول اليوم مع الجلسات مرتبة حسب وقت البدء',
        'تنبيهات الحضور المنخفض (< 75%) لجلسات اليوم الماضي',
        'البحث العالمي عن الطلاب من شريط البحث',
        'تحديث تلقائي كل 5 دقائق',
      ],
      tips: [
        'انقر على أي بطاقة مؤشر للانتقال مباشرة إلى القسم المقابل.',
        'أداة الديون الحمراء تعني أكثر من 5000 دج غير مدفوعة — تصرف فوراً.',
        'استخدم زر "تحديث" لإجبار تحديث البيانات فوراً.',
      ],
      steps: [
        'راجع بطاقات المؤشرات الأربع للحصول على نظرة عامة سريعة.',
        'تحقق من أداة الديون: أخضر = صحي، برتقالي = تحذير، أحمر = حرج.',
        'تصفح جدول اليوم وسجّل الحضور المفقود.',
        'تعامل مع تنبيهات انخفاض الحضور في الأسفل.',
      ],
    },
    students: {
      title: 'الطلاب',
      description: 'أدر جميع طلابك المسجلين: الإنشاء، التعديل، المتابعة المالية وسجل الحضور.',
      features: [
        'قائمة مُرقَّمة وقابلة للترتيب لجميع الطلاب',
        'ملف فردي: بيانات شخصية، المجموعة (المجموعات) المخصصة',
        'متابعة المدفوعات لكل طالب (المبلغ المستحق مقابل المدفوع)',
        'سجل الحضور لكل طالب',
        'بحث سريع بالاسم أو رقم الهاتف',
        'إضافة وتعديل عبر نموذج متكامل',
      ],
      tips: [
        'استخدم البحث العالمي للعثور على طالب من أي صفحة.',
        'يمكن تسجيل الطالب في عدة مجموعات في آنٍ واحد.',
        'حقل "الحالة" يُسهّل تصفية النشطين من غير النشطين.',
      ],
    },
    teachers: {
      title: 'المعلمون',
      description: 'مركزة إدارة المعلمين: الملفات، التوافر، وتعيين المجموعات.',
      features: [
        'قائمة كاملة بالمعلمين مع حالة نشط / غير نشط',
        'ملف المعلم: بيانات الاتصال، التخصص، المجموعات المخصصة',
        'تصفية حسب الحالة والتخصص',
        'رابط مباشر إلى جدول معلم معين',
      ],
      tips: [
        'عطّل معلماً بدلاً من حذفه للحفاظ على السجل التاريخي.',
        'المعلم غير النشط لن يظهر في قوائم الاختيار في المُجدوِل.',
      ],
    },
    groups: {
      title: 'المجموعات',
      description: 'نظّم طلابك في مجموعات دراسية. تُعرَّف كل مجموعة بمادة، ومستوى، ومعلم، وقائمة طلاب مسجلين.',
      features: [
        'إنشاء وتعديل المجموعات',
        'تعيين معلم مسؤول لكل مجموعة',
        'قائمة الطلاب الأعضاء في المجموعة',
        'رسم شهري لكل مجموعة (يُستخدم للفوترة التلقائية)',
        'مؤشر عدد الجلسات المخطط لها',
      ],
      tips: [
        'حدّد رسماً شهرياً لكل مجموعة حتى تحسب الوحدة المالية الديون تلقائياً.',
        'سمِّ المجموعات بأسماء واضحة لتسهيل قراءة الجدول.',
      ],
    },
    rooms: {
      title: 'القاعات',
      description: 'أدر قاعات الدروس المتاحة في أكاديميتك. يتحقق النظام من تعارضات الحجز عند إنشاء الجلسات.',
      features: [
        'قائمة القاعات مع الرقم، الطابق والسعة',
        'الحالة: نشط / غير نشط',
        'كشف تلقائي للتعارضات أثناء التخطيط',
        'مؤشر الاستخدام (عدد الجلسات المجدولة)',
      ],
      tips: [
        'ضع حالة القاعة "غير نشطة" أثناء الصيانة لمنع الحجوزات.',
        'السعة استرشادية فقط ولا تمنع الحجز الزائد.',
      ],
    },
    scheduler: {
      title: 'المُجدوِل',
      description: 'خطّط وأدر جميع جلسات الدروس. يكتشف المُجدوِل تعارضات القاعات والمعلمين في الوقت الفعلي.',
      features: [
        'عرض التقويم: أسبوعي أو يومي',
        'إنشاء جلسات مع المجموعة، القاعة، المعلم، والوقت',
        'كشف تلقائي للتعارضات (قاعة أو معلم مشغول بالفعل)',
        'تحديد "تم تسجيل الحضور" لكل جلسة',
        'تصفية حسب المجموعة أو القاعة أو المعلم',
      ],
      tips: [
        'لا يمكن إنشاء جلسة إذا كانت القاعة أو المعلم مشغولَين في ذلك الوقت.',
        'تظهر جلسات اليوم تلقائياً في لوحة التحكم.',
        'ضع علامة "تم تسجيل الحضور" مباشرة من المُجدوِل بعد كل جلسة.',
      ],
      steps: [
        'انقر على وقت فارغ في التقويم.',
        'اختر المجموعة والقاعة والمعلم.',
        'حدّد وقت البدء والانتهاء.',
        'احفظ — يتحقق النظام من غياب التعارضات.',
      ],
    },
    financial: {
      title: 'المالية',
      description: 'تابع المدفوعات والديون وإيرادات الأكاديمية. تحسب الوحدة المالية تلقائياً ما يدين به كل طالب.',
      features: [
        'نظرة عامة على إيرادات الشهر الجاري',
        'قائمة الطلاب المدينين مع المبلغ المستحق',
        'تسجيل المدفوعات (جزئية أو كاملة)',
        'سجل كامل للمعاملات لكل طالب',
        'مؤشر إجمالي الديون (يظهر في لوحة التحكم)',
      ],
      tips: [
        'تُظهر لوحة التحكم إجمالي الديون باللون الأحمر عند تجاوز 5000 دج.',
        'سجّل المدفوعات الجزئية للحفاظ على دقة المتابعة.',
        'استخدم الفلاتر لعزل الطلاب الذين لديهم دين > 0.',
      ],
    },
    attendance: {
      title: 'الحضور',
      description: 'سجّل وراجع الحضور لكل جلسة دروس. يمكن تمييز كل طالب بـ: حاضر، غائب، أو متأخر.',
      features: [
        'اختيار جلسة اليوم لتسجيل الحضور',
        'قائمة تلقائية بطلاب المجموعة',
        'الحالات: حاضر / غائب / متأخر',
        'إجراء سريع "تحديد الكل حاضر"',
        'سجل الحضور لكل طالب',
        'نسبة الحضور محسوبة تلقائياً لكل جلسة',
      ],
      tips: [
        'يمكنك الوصول إلى الحضور مباشرة من لوحة التحكم.',
        'الجلسات ذات الحضور الأقل من 75% تُطلق تنبيهاً في اليوم التالي.',
        'يجب إنشاء الجلسة في المُجدوِل قبل تسجيل الحضور.',
      ],
      steps: [
        'انتقل إلى وحدة الحضور.',
        'اختر التاريخ والجلسة المطلوبة.',
        'ضع علامة على حالة كل طالب.',
        'احفظ — تُحدَّد الجلسة تلقائياً بـ "تم تسجيل الحضور".',
      ],
    },
    reports: {
      title: 'التقارير',
      description: 'أنشئ تقارير وإحصاءات حول نشاط الأكاديمية.',
      features: [
        'تقرير الحضور عام أو حسب المجموعة / الطالب',
        'التقرير المالي: الإيرادات، الديون، المدفوعات حسب الفترة',
        'تقرير نشاط المعلمين',
        'تصدير البيانات متاح',
      ],
      tips: [
        'صفِّ حسب الفترة (شهر، فصل) للحصول على تحليلات دقييقة.',
        'شارك تقارير الحضور مع الأولياء لمتابعة شفافة.',
      ],
    },
    'pre-registrations': {
      title: 'التسجيلات المسبقة',
      description: 'أدر طلبات الترشح قبل القبول النهائي. كل طلب يتبع مساراً: مُقدَّم ← مقبول ← محوَّل إلى طالب (أو مرفوض).',
      features: [
        'نموذج تسجيل مسبق كامل (بيانات الاتصال، الدورة المرغوبة)',
        'الحالات: قيد الانتظار / مقبول / مرفوض',
        'تحويل بنقرة واحدة إلى ملف طالب كامل (إذا قُبِل)',
        'إرسال بريد إلكتروني تلقائي للمرشح بناءً على القرار',
      ],
      tips: [
        'اقبل الطلب ثم انقر "تحويل" لإنشاء ملف الطالب تلقائياً.',
        'يُرسَل بريد إلكتروني إذا كان النموذج المقابل مُفعَّلاً.',
      ],
      steps: [
        'افتح الطلب المُستلَم.',
        'راجع المعلومات وأضف ملاحظات داخلية إن لزم.',
        'انقر "قبول" أو "رفض".',
        'إذا قُبِل، انقر "تحويل إلى طالب" لإتمام التسجيل.',
      ],
    },
    'room-rentals': {
      title: 'تأجير القاعات',
      description: 'أدر تأجير القاعات لعملاء خارجيين خارج الحصص الأكاديمية. يتحقق النظام من التعارضات مع الجدول الموجود.',
      features: [
        'حجز قاعة لعميل خارجي مع فترة زمنية',
        'كشف تلقائي للتعارضات مع الجدول الأكاديمي',
        'ملف العميل المستأجر (الاسم، التواصل، الأجر)',
        'عرض التوافر الفوري',
      ],
      tips: [
        'تظهر القاعة مشغولة في الجدول الأكاديمي طوال فترة الإيجار.',
        'حدّد سعر الإيجار لمتابعة مالية دقيقة.',
      ],
    },
    discounts: {
      title: 'التخفيضات والخصومات',
      description: 'أنشئ وأدر أنواع الخصومات المطبَّقة على رسوم الطلاب. كل خصم يُحدَّد بنوعه (نسبة مئوية أو مبلغ ثابت) وفترة صلاحيته.',
      features: [
        'خصومات بنسبة مئوية أو مبلغ ثابت',
        'تواريخ الصلاحية (بداية / نهاية)',
        'تطبيق لكل طالب من بطاقة التسعير',
        'مسار الموافقة: مُقدَّم ← موافق عليه ← مطبَّق على الفاتورة',
        'سجل الخصومات الممنوحة لكل طالب',
      ],
      tips: [
        'يجب أن يوافق مسؤول على الخصم قبل تفعيله.',
        'راجع صفحة "التحقق من الخصومات" لمعالجة الطلبات المعلقة.',
        'لا يمكن تطبيق خصم منتهي الصلاحية حتى لو كان نشطاً.',
      ],
    },
    quizzes: {
      title: 'الاختبارات والتقييمات',
      description: 'أنشئ تقييمات تفاعلية لمجموعاتك. يجيب الطلاب على الأسئلة وتُسجَّل درجاتهم تلقائياً.',
      features: [
        'إنشاء اختبار بعنوان، ومستوى، وحد زمني، ودرجة النجاح',
        'أنواع الأسئلة: اختيار متعدد، صح/خطأ، إجابة حرة',
        'تفعيل / إلغاء تفعيل الاختبار للطلاب',
        'عرض المحاولات والدرجات لكل طالب',
        'إحصاءات النجاح لكل اختبار',
      ],
      tips: [
        'حدّد درجة النجاح لتمييز الناجحين تلقائياً.',
        'عطّل الاختبار لتعديله دون التأثير على الطلاب الحاليين.',
        'راجع المحاولات لتحديد الأسئلة ذات أعلى نسبة أخطاء.',
      ],
      steps: [
        'أنشئ الاختبار (العنوان، المستوى، المدة).',
        'أضف الأسئلة واحدة تلو الأخرى من تبويب الأسئلة.',
        'فعِّل الاختبار — يصبح مرئياً للطلاب المعيَّنين.',
        'راجع النتائج في "المحاولات".',
      ],
    },
    'email-templates': {
      title: 'نماذج البريد الإلكتروني',
      description: 'خصِّص الرسائل الإلكترونية التلقائية للأكاديمية (OTP، تذكيرات الديون، الإشعارات). النماذج كيانات نظام: قابلة للتعديل والتفعيل لكن لا تُحذف.',
      features: [
        'نماذج محددة مسبقاً: OTP، تذكير ديون البالغين، تذكير ديون الأطفال',
        'محرر HTML غني بالتنسيق (عريض، قوائم، جداول)',
        'متغيرات ديناميكية قابلة للنقر: {student.fullName}، {agencyName}، إلخ',
        'معاينة مباشرة بببيانات تجريبية',
        'دعم متعدد اللغات: نموذج لكل نوع ولغة (FR/EN/AR)',
        'تفعيل / إلغاء تفعيل لكل نموذج',
      ],
      tips: [
        'انقر على متغير في اللوحة لإدراجه مباشرة في نص HTML.',
        'يُسمح بنموذج واحد فقط لكل مزيج (نوع + لغة) — عدِّل الموجود.',
        'النموذج غير النشط يُتجاهَل من النظام التلقائي للإرسال.',
      ],
      steps: [
        'اختر النموذج المطلوب تعديله من القائمة.',
        'عدِّل الموضوع ونص HTML.',
        'استخدم المتغيرات من اللوحة الجانبية لتخصيص المحتوى.',
        'فعِّل النموذج واحفظ.',
      ],
    },
    'email-log': {
      title: 'سجل البريد الإلكتروني',
      description: 'اطَّلع على السجل الكامل لجميع الرسائل الإلكترونية المرسلة. حدِّد الإخفاقات وأعد الإرسال عند الضرورة.',
      features: [
        'سجل مُؤرَّخ لكل بريد مُرسَل',
        'الحالات: مُرسَل ✅ / فشل ❌ / في الانتظار 🕐',
        'تفاصيل الخطأ عند الفشل (كود SMTP، الرسالة)',
        'فلاتر حسب الحالة، النوع والفترة',
        'زر "إعادة الإرسال" للرسائل الفاشلة',
      ],
      tips: [
        'افحص السجل بانتظام للكشف عن مشاكل إرسال SMTP.',
        'يمكن إعادة إرسال بريد فاشل يدوياً من هذه الصفحة.',
      ],
    },
    'planning-controls': {
      title: 'ضوابط الجدول',
      description: 'اضبط قواعد التحقق التلقائي من الجدول. يمكن أن تكون كل قاعدة حاجبة (ترفض الجلسة) أو غير حاجبة (تحذير فقط). هذه كيانات نظام: قابلة للتعديل لكن لا تُحذف.',
      features: [
        'قائمة قواعد التحقق من الجدول',
        'تفعيل / إلغاء تفعيل كل قاعدة',
        'السلوك: حاجب (رفض) أو تحذير (إرشادي)',
        'قواعد محددة مسبقاً: تعارض القاعة، تعارض المعلم، تجاوز السعة',
        'إمكانية إضافة قواعد مخصصة',
      ],
      tips: [
        'عطّل قاعدة مؤقتاً للسماح باستثناء دون حذفها.',
        'حوِّل قاعدة إلى "غير حاجبة" إذا كنت تفضل تحذيراً بدلاً من الرفض.',
        'قواعد النظام المدمجة لا يمكن حذفها — التعديل فقط مسموح.',
      ],
    },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// App registry — maps ApplicationName → { sections, content, headerColor }
// ─────────────────────────────────────────────────────────────────────────────

interface AppManualConfig {
  sections: ManualSection[];
  content: AppManualContent;
  headerGradient: string;
  appIcon: React.ReactNode;
}

const APP_MANUALS: Partial<Record<ApplicationName, AppManualConfig>> = {
  Academy: {
    sections: ACADEMY_SECTIONS,
    content: ACADEMY_CONTENT,
    headerGradient: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)',
    appIcon: <SchoolIcon sx={{ fontSize: '1.6rem' }} />,
  },
  CRM: {
    sections: CRM_SECTIONS,
    content: CRM_CONTENT,
    headerGradient: 'linear-gradient(135deg, #0f172a 0%, #1e293b 45%, #0369a1 100%)',
    appIcon: <CrmIcon sx={{ fontSize: '1.6rem' }} />,
  },
  ProjectManagement: {
    sections: PROJECT_SECTIONS,
    content: PROJECT_CONTENT,
    headerGradient: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 45%, #4338ca 100%)',
    appIcon: <ProjectIcon sx={{ fontSize: '1.6rem' }} />,
  },
  // ── Future modules — add sections/content here when ready ──────────────────
  Planning: {
    sections: [{ id: 'overview', icon: <PlanningIcon />, color: '#0369a1' }],
    content: {
      fr: { overview: { title: 'Planning', description: 'Manuel en cours de rédaction. Disponible prochainement.', features: [], tips: [] } },
      en: { overview: { title: 'Planning', description: 'Manual under construction. Coming soon.', features: [], tips: [] } },
      ar: { overview: { title: 'التخطيط', description: 'الدليل قيد الإعداد. قريباً.', features: [], tips: [] } },
    },
    headerGradient: 'linear-gradient(135deg, #0c4a6e 0%, #0369a1 50%, #0284c7 100%)',
    appIcon: <PlanningIcon sx={{ fontSize: '1.6rem' }} />,
  },
  Parc: {
    sections: [{ id: 'overview', icon: <ParcIcon />, color: '#065f46' }],
    content: {
      fr: { overview: { title: 'Parc', description: 'Manuel en cours de rédaction. Disponible prochainement.', features: [], tips: [] } },
      en: { overview: { title: 'Fleet', description: 'Manual under construction. Coming soon.', features: [], tips: [] } },
      ar: { overview: { title: 'الأسطول', description: 'الدليل قيد الإعداد. قريباً.', features: [], tips: [] } },
    },
    headerGradient: 'linear-gradient(135deg, #064e3b 0%, #065f46 50%, #047857 100%)',
    appIcon: <ParcIcon sx={{ fontSize: '1.6rem' }} />,
  },
  Stock: {
    sections: [{ id: 'overview', icon: <StockIcon />, color: '#92400e' }],
    content: {
      fr: { overview: { title: 'Stock', description: 'Manuel en cours de rédaction. Disponible prochainement.', features: [], tips: [] } },
      en: { overview: { title: 'Stock', description: 'Manual under construction. Coming soon.', features: [], tips: [] } },
      ar: { overview: { title: 'المخزون', description: 'الدليل قيد الإعداد. قريباً.', features: [], tips: [] } },
    },
    headerGradient: 'linear-gradient(135deg, #78350f 0%, #92400e 50%, #b45309 100%)',
    appIcon: <StockIcon sx={{ fontSize: '1.6rem' }} />,
  },
  Maker: {
    sections: [{ id: 'overview', icon: <MakerIcon />, color: '#1e1e1e' }],
    content: {
      fr: { overview: { title: 'Maker', description: 'Manuel en cours de rédaction. Disponible prochainement.', features: [], tips: [] } },
      en: { overview: { title: 'Maker', description: 'Manual under construction. Coming soon.', features: [], tips: [] } },
      ar: { overview: { title: 'الإنتاج', description: 'الدليل قيد الإعداد. قريباً.', features: [], tips: [] } },
    },
    headerGradient: 'linear-gradient(135deg, #111827 0%, #1f2937 50%, #374151 100%)',
    appIcon: <MakerIcon sx={{ fontSize: '1.6rem' }} />,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// UI label strings per language
// ─────────────────────────────────────────────────────────────────────────────

const UI_LABELS: Record<LangKey, Record<string, string>> = {
  fr: {
    title: 'Manuel Utilisateur',
    subtitle: 'Guide complet de toutes les fonctionnalités',
    features: 'Fonctionnalités',
    steps: 'Étapes',
    tips: 'Conseils',
    comingSoon: 'Manuel en cours de rédaction',
    comingSoonSub: 'Ce manuel sera disponible prochainement.',
  },
  en: {
    title: 'User Manual',
    subtitle: 'Complete guide to all features',
    features: 'Features',
    steps: 'Steps',
    tips: 'Tips',
    comingSoon: 'Manual under construction',
    comingSoonSub: 'This manual will be available soon.',
  },
  ar: {
    title: 'دليل المستخدم',
    subtitle: 'دليل شامل لجميع الميزات',
    features: 'الميزات',
    steps: 'الخطوات',
    tips: 'نصائح',
    comingSoon: 'الدليل قيد الإعداد',
    comingSoonSub: 'سيكون هذا الدليل متاحاً قريباً.',
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

interface UserManualProps {
  open: boolean;
  onClose: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export const UserManual: React.FC<UserManualProps> = ({ open, onClose }) => {
  const { language, dir } = useLanguage();
  const { currentApp } = useApplication();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const lang: LangKey = (['fr', 'en', 'ar'] as LangKey[]).includes(language as LangKey)
    ? (language as LangKey)
    : 'fr';

  // Resolve the active app config (fall back to a generic placeholder)
  const appName: ApplicationName = currentApp ?? 'Academy';
  const appConfig = APP_MANUALS[appName] ?? APP_MANUALS['Academy']!;
  const { sections, content, headerGradient, appIcon } = appConfig;

  const [activeSection, setActiveSection] = useState<string>(sections[0]?.id ?? '');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Reset active section when app changes
  React.useEffect(() => {
    setActiveSection(sections[0]?.id ?? '');
  }, [appName, sections]);

  const labels = UI_LABELS[lang];
  const sectionDef = sections.find(s => s.id === activeSection) ?? sections[0];
  const sectionContent = content[lang]?.[activeSection];
  const isRtl = dir === 'rtl';

  const handleSectionClick = (id: string) => {
    setActiveSection(id);
    setMobileNavOpen(false);
  };

  // ── Sidebar ─────────────────────────────────────────────────────────────────
  const SidebarNav = (
    <Box
      sx={{
        width: isMobile ? '100%' : 220,
        flexShrink: 0,
        borderRight: isRtl ? 'none' : '1px solid',
        borderLeft: isRtl ? '1px solid' : 'none',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflowY: 'auto',
        display: isMobile && !mobileNavOpen ? 'none' : 'block',
      }}
    >
      <List dense disablePadding>
        {sections.map((s) => {
          const sc = content[lang]?.[s.id];
          const isActive = activeSection === s.id;
          return (
            <ListItem key={s.id} disablePadding>
              <ListItemButton
                selected={isActive}
                onClick={() => handleSectionClick(s.id)}
                sx={{
                  py: 1.2,
                  px: 2,
                  borderRadius: 0,
                  transition: 'all 0.18s',
                  borderLeft: (!isRtl && isActive) ? `4px solid ${s.color}` : '4px solid transparent',
                  borderRight: (isRtl && isActive) ? `4px solid ${s.color}` : '4px solid transparent',
                  bgcolor: isActive ? `${s.color}18` : 'transparent',
                  '&:hover': { bgcolor: `${s.color}10` },
                  '&.Mui-selected': { bgcolor: `${s.color}18` },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 34,
                    color: isActive ? s.color : 'text.secondary',
                    '& svg': { fontSize: '1.1rem' },
                  }}
                >
                  {s.icon}
                </ListItemIcon>
                <ListItemText
                  primary={sc?.title ?? s.id}
                  primaryTypographyProps={{
                    fontSize: '0.82rem',
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? s.color : 'text.primary',
                    noWrap: true,
                  }}
                />
                {isActive && !isMobile && (
                  <ArrowIcon sx={{ fontSize: '0.7rem', color: s.color, ml: 0.5, transform: isRtl ? 'scaleX(-1)' : 'none' }} />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  // ── Coming-soon placeholder ─────────────────────────────────────────────────
  const ComingSoonPanel = (
    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        gap: 2,
        color: 'text.secondary',
      }}
    >
      <ComingSoonIcon sx={{ fontSize: 64, opacity: 0.3 }} />
      <Typography variant="h6" fontWeight={700} color="text.secondary">
        {labels.comingSoon}
      </Typography>
      <Typography variant="body2" color="text.disabled">
        {labels.comingSoonSub}
      </Typography>
    </Box>
  );

  // ── Content panel ────────────────────────────────────────────────────────────
  const ContentPanel =
    !sectionContent || (!sectionContent.features.length && !sectionContent.steps?.length && !sectionContent.tips.length) ? (
      ComingSoonPanel
    ) : (
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: { xs: 2, md: 4 },
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {/* Section header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: `${sectionDef.color}22`,
              color: sectionDef.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& svg': { fontSize: '2rem' },
            }}
          >
            {sectionDef.icon}
          </Box>
          <Typography variant="h5" fontWeight={800} color={sectionDef.color}>
            {sectionContent.title}
          </Typography>
        </Box>

        {/* Description */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 3,
            bgcolor: `${sectionDef.color}0d`,
            border: `1px solid ${sectionDef.color}30`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <InfoIcon sx={{ color: sectionDef.color, mt: 0.2, flexShrink: 0 }} />
            <Typography variant="body1" color="text.primary" lineHeight={1.75}>
              {sectionContent.description}
            </Typography>
          </Box>
        </Paper>

        {/* Features */}
        {sectionContent.features.length > 0 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <StarIcon sx={{ color: sectionDef.color, fontSize: '1rem' }} />
              <Typography variant="subtitle1" fontWeight={700}>{labels.features}</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {sectionContent.features.map((feat, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <Chip
                    label={i + 1}
                    size="small"
                    sx={{
                      bgcolor: sectionDef.color,
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      minWidth: 24,
                      height: 22,
                      mt: 0.15,
                      flexShrink: 0,
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" lineHeight={1.65} sx={{ pt: 0.1 }}>
                    {feat}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Steps */}
        {sectionContent.steps && sectionContent.steps.length > 0 && (
          <Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <ManualIcon sx={{ color: sectionDef.color, fontSize: '1rem' }} />
              <Typography variant="subtitle1" fontWeight={700}>{labels.steps}</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              {sectionContent.steps.map((step, i) => (
                <Paper
                  key={i}
                  elevation={0}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: sectionDef.color,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: '0.8rem',
                      flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Typography variant="body2" color="text.primary" lineHeight={1.7} sx={{ pt: 0.3 }}>
                    {step}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        {/* Tips */}
        {sectionContent.tips.length > 0 && (
          <Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <TipIcon sx={{ color: '#f59e0b', fontSize: '1rem' }} />
              <Typography variant="subtitle1" fontWeight={700}>{labels.tips}</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {sectionContent.tips.map((tip, i) => (
                <Paper
                  key={i}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: '#fef3c708',
                    border: '1px solid #f59e0b40',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                  }}
                >
                  <TipIcon sx={{ color: '#f59e0b', fontSize: '1rem', mt: 0.25, flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary" lineHeight={1.65}>
                    {tip}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );

  // ── Dialog ───────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile || isFullscreen}
      PaperProps={{
        sx: {
          borderRadius: (isMobile || isFullscreen) ? 0 : 3,
          overflow: 'hidden',
          height: (isMobile || isFullscreen) ? '100%' : '82vh',
          maxHeight: (isMobile || isFullscreen) ? '100%' : '82vh',
          m: (isMobile || isFullscreen) ? 0 : 2,
          direction: isRtl ? 'rtl' : 'ltr',
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          background: headerGradient,
          color: 'white',
          px: { xs: 2, md: 3 },
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {appIcon}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
              {labels.title} — {appName}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              {labels.subtitle}
            </Typography>
          </Box>
        </Box>

        {/* Mobile: current section chip */}
        {isMobile && sectionContent && (
          <Chip
            label={sectionContent.title}
            size="small"
            onClick={() => setMobileNavOpen(v => !v)}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontWeight: 700,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
            }}
          />
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
          {/* Fullscreen toggle — hidden on mobile (already full screen) */}
          {!isMobile && (
            <IconButton
              onClick={() => setIsFullscreen(v => !v)}
              size="small"
              sx={{
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
              }}
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          )}

          <IconButton
            onClick={onClose}
            sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* ── Body: sidebar + content ── */}
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          overflow: 'hidden',
          flex: 1,
        }}
      >
        {SidebarNav}
        {(!isMobile || !mobileNavOpen) && ContentPanel}
      </DialogContent>
    </Dialog>
  );
};

export default UserManual;
